'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { clsx } from 'clsx'
import styles from './MenuHeaderNavigation.module.scss'
import NTVLogo from '@/components/Logo/NTVLogo'
import { Link } from '@/components/Link'
import { AreaItem } from '@/types/cms/AreaItem'
import PushButton from '@/components/PushButton/PushButton'
import AppContext from '@/types/context'
import { DarkLightModeToggle } from '@/components/DarkLightModeToggle'
import { Icon } from '@/components/Icon'

type MenuHeaderNavigationProps = {
  item: AreaItem
  context: AppContext
}

// upward scroll distance before bar is shown again
const SCROLL_UP_THRESHOLD = 80
// bar height, while below offset its still sticky
const NAVIGATION_HEIGHT = 40

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

const MenuHeaderNavigation = ({ item, context }: MenuHeaderNavigationProps) => {
  const [isPinned, setIsPinned] = useState(false)
  const lastScrollYRef = useRef(0)
  const scrolledUpDistanceRef = useRef(0)

  useEffect(() => {
    if (typeof globalThis === 'undefined') return

    lastScrollYRef.current = globalThis.scrollY ?? 0

    const handleScroll = () => {
      const currentScrollY = globalThis.scrollY ?? 0
      const diff = currentScrollY - lastScrollYRef.current
      lastScrollYRef.current = currentScrollY

      // unpinning above 0 would move the bar out of view again, which looks like a jump
      if (currentScrollY <= 0 || diff > 0) {
        scrolledUpDistanceRef.current = 0
        setIsPinned(false)
        return
      }

      if (diff < 0) {
        scrolledUpDistanceRef.current -= diff

        if (scrolledUpDistanceRef.current >= SCROLL_UP_THRESHOLD) {
          setIsPinned(true)
        }
      }
    }

    globalThis.addEventListener('scroll', handleScroll, { passive: true })
    return () => globalThis.removeEventListener('scroll', handleScroll)
  }, [])

  // keeps the sticky main header below the pinned bar
  useIsomorphicLayoutEffect(() => {
    const root = globalThis.document?.documentElement
    if (!root) return

    root.style.setProperty('--menu-nav-pinned-offset', isPinned ? `${NAVIGATION_HEIGHT}px` : '0px')

    return () => {
      root.style.removeProperty('--menu-nav-pinned-offset')
    }
  }, [isPinned])

  return (
    // wrapper keeps the 40px slot in the flow while the bar is fixed
    <div className={styles.wrapper}>
      <nav className={clsx(styles.navigation, isPinned && styles.pinned)} data-cy="widget-menu-header-navigation">
        <div className={styles.container}>
          <div className={styles['left-block']}>
            <Link href="/" className={styles.link}>
              <Icon name="arrowLeft" size={18} />
              <span>ntv.de</span>
            </Link>
          </div>
          <div className={styles['right-block']}>
            <DarkLightModeToggle context={context} />
            <PushButton context={context} />
          </div>
        </div>
      </nav>
    </div>
  )
}

export default MenuHeaderNavigation
